import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { cn } from "../lib/helpers";

import PortableText from "./portableText";
import PageSections from "./page-sections";

import { bodyTextBlock } from "./typography.module.css";
import styles from "./contact-page.module.css";

function encode(data) {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
}

const ContactPage = (props) => {
  const {
    contactTitle,
    contactTitleColor,
    email,
    location,
    generalContactColor,
    mainContactEmail,
    mainContactName,
    mainContactTitle,
    mainContactColor,
    phone,
    _rawContactBodyCopy,
    contactBodyCopy,
    _rawContactPageSections,
    contactPageSections,
  } = props;

  // Same raw-vs-plain fallback as sectionContent below: Gatsby's GraphQL
  // layer supplies `_rawContactBodyCopy`, but the live-preview page's GROQ
  // query returns the plain `contactBodyCopy` field instead (GROQ has no
  // `_raw` naming convention) — without this fallback the body copy only
  // ever showed up on the real built site, never in preview.
  const bodyCopyBlocks = _rawContactBodyCopy ? _rawContactBodyCopy : contactBodyCopy;

  const sectionContent = _rawContactPageSections ? _rawContactPageSections : contactPageSections;

  // Unset (the common case today, since existing siteSettings documents
  // predate this field) falls through to no extra class, which keeps the
  // current default color set in contact-page.module.css's `.description h2`
  // rule — this is purely additive, nothing changes until Ellerey picks a
  // preset in Studio.
  const titleColorClass =
    contactTitleColor === "cyan"
      ? styles.titleColorCyan
      : contactTitleColor === "grey"
      ? styles.titleColorGrey
      : contactTitleColor === "cream"
      ? styles.titleColorCream
      : "";

  // Same unset-falls-through-to-current-default pattern as titleColorClass
  // above, for the two new Studio color selectors added 2026-09-11.
  const generalContactColorClass =
    generalContactColor === "cyan"
      ? styles.generalContactColorCyan
      : generalContactColor === "grey"
      ? styles.generalContactColorGrey
      : generalContactColor === "cream"
      ? styles.generalContactColorCream
      : "";

  const mainContactColorClass =
    mainContactColor === "cyan"
      ? styles.mainContactColorCyan
      : mainContactColor === "grey"
      ? styles.mainContactColorGrey
      : mainContactColor === "cream"
      ? styles.mainContactColorCream
      : "";

  const { register, handleSubmit, errors } = useForm();

  const [formSubmitted, setFormSubmitted] = useState(false);
  const onSubmit = (data, event) => {
    const form = event.target;
    form.reset();
    const request = {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({
        "form-name": form.getAttribute("name"),
        ...data,
      }),
    };
    fetch("/", request)
      .then(submitSuccess())
      .catch((error) => alert(error));
  };

  const submitSuccess = () => {
    setFormSubmitted(true);
  };

  return (
    <div className={styles.root}>
      <div
        className={cn(
          styles.inner,
          sectionContent && !!sectionContent.length && styles.hasSections
        )}
      >
        <div className={styles.description}>
          {contactTitle && <h2 className={cn(titleColorClass)}>{contactTitle}</h2>}
          {bodyCopyBlocks && (
            <div className={cn(bodyTextBlock, styles.contactBodyCopy)}>
              <PortableText blocks={bodyCopyBlocks} />
            </div>
          )}
        </div>
        <div className={styles.companyContact}>
          <div className={cn(styles.mainContact, mainContactColorClass)}>
            <div>
              {mainContactName && (
                <span className={styles.mainContactName}>{mainContactName}</span>
              )}
              {mainContactTitle && (
                <span className={styles.mainContactTitle}>, {mainContactTitle}</span>
              )}
            </div>
            {mainContactEmail && (
              <a className={styles.mainContactEmail} href={`mailto:${mainContactEmail}`}>
                {mainContactEmail}
              </a>
            )}
          </div>
          <div className={cn(styles.companyInfo, generalContactColorClass)}>
            {location && <div className={styles.location}>{location}</div>}
            {phone && <div className={styles.phone}>{phone}</div>}
            {email && (
              <a className={styles.companyEmail} href={`mailto:${email}`}>
                {email}
              </a>
            )}
          </div>
        </div>
        <form
          name="contact"
          method="post"
          onSubmit={handleSubmit(onSubmit)}
          className={styles.form}
        >
          {/* The `form-name` hidden field is required to support form submissions without JavaScript */}
          <input type="hidden" name="form-name" value="contact" />
          <div hidden>
            <label>
              Don’t fill this out: <input name="bot-field" />
            </label>
          </div>
          <div className={styles.formRow}>
            <label className={styles.field}>
              <input
                type="text"
                name="name"
                placeholder="Name"
                aria-label="Name"
                ref={register({ required: true })}
              />
              {errors.name && <span className="error">Please enter your name</span>}
            </label>
            <label className={styles.field}>
              <input
                type="email"
                name="email"
                placeholder="Email"
                aria-label="Email"
                ref={register({ required: true })}
              />
              {errors.email && <span className="error">Please enter an email</span>}
            </label>
          </div>
          <label className={styles.field}>
            <input type="text" name="subject" placeholder="Subject" aria-label="Subject" ref={register} />
          </label>
          <label className={styles.field}>
            <textarea
              name="message"
              placeholder="Message"
              aria-label="Message"
              ref={register({ required: true })}
            />
            {errors.message && <span className="error">Please enter a message</span>}
          </label>
          <div className={styles.sendRow}>
            {email && <div className={styles.sendingTo}>Sending to: {email}</div>}
            <button
              className={cn(styles.submitForm, formSubmitted && styles.formSubmitted)}
              type="submit"
            >
              <span>{!formSubmitted ? "Send" : "Thanks!"}</span>
            </button>
          </div>
        </form>
      </div>
      {sectionContent && !!sectionContent.length && (
        // No `hasPaddingTop` here on purpose — the Send button already reads
        // as a clean stopping point, so sections butt right up against the
        // form with zero added gap. Add a blank Text Section block above
        // any future content to bring manual spacing back if it's ever needed.
        <PageSections content={sectionContent} />
      )}
    </div>
  );
};
export default ContactPage;
